import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { getAssetUrls } from '../../api';

/**
 * Signed display URLs for the assets currently on screen.
 *
 * Signed URLs can't live on the asset rows: each one costs an IAM signing
 * round trip and expires, so they are fetched alongside the list rather than
 * embedded in it. The backend signs a whole page in one batched, concurrent
 * call — the point of that endpoint is that the grid asks once for fifty
 * thumbnails instead of fifty times for one.
 *
 * Expiry is handled by asking the server rather than by guessing. The backend
 * serves URLs out of its own cache, so what arrives here is not freshly signed
 * and can have far less than the full hour left on it — `expires_in_seconds`
 * is the only honest figure. Recovery is event-driven: nothing polls, and a
 * URL that dies before its image is fetched is reported by the card that
 * failed to load it (see `refresh`).
 */

/**
 * Re-sign a little before the URL actually dies. Signing on the exact expiry
 * would hand out URLs that die mid-render; five minutes of headroom covers the
 * gap between the response landing here and the browser fetching the image.
 */
const EXPIRY_HEADROOM_MS = 5 * 60 * 1000;

/** Only used before the first response tells us the real figure. */
const DEFAULT_URL_TTL_MS = 60 * 60 * 1000;

export const ASSET_URLS_QUERY_KEY = ['asset-library', 'asset-urls'] as const;

/**
 * How long a signed-URL response can be trusted.
 *
 * Derived from the response rather than assumed: the backend serves URLs from
 * its own cache, whose TTL is shorter than the signature's validity but not by
 * much, so what arrives here can have as little as a minute or two left on it.
 * Treating that as a fresh hour is exactly what leaves dead URLs on screen.
 *
 * Clamped at zero so a response that is already inside the headroom window is
 * immediately stale rather than negative.
 */
export function signedUrlStaleTime(expiresInSeconds: number | undefined): number {
  if (expiresInSeconds === undefined) return DEFAULT_URL_TTL_MS;
  return Math.max(0, expiresInSeconds * 1000 - EXPIRY_HEADROOM_MS);
}

export interface AssetUrlLookup {
  /** assetId -> signed URL of the original. Absent means "no URL", not "still loading". */
  urlById: Map<string, string>;
  /**
   * assetId -> signed URL of its grid thumbnail, for the assets that have one.
   * Anything showing an asset small reads this first (`smallUrlOf`); the
   * original can be tens of megabytes, or a film.
   */
  thumbnailById: Map<string, string>;
  /** Ids the backend could not resolve — render a fallback tile, not a spinner. */
  missing: Set<string>;
  isLoading: boolean;
  /**
   * Re-sign the current page. Called by a card whose image failed to load,
   * which is the only reliable signal that a URL expired early — an expired
   * URL is indistinguishable from a live one until the browser fetches it.
   *
   * Safe to call from many cards at once: they share one query key, and
   * concurrent invalidations of a key in flight collapse into one request.
   */
  refresh: () => void;
}

export function useAssetUrls(assetIds: string[]): AssetUrlLookup {
  const queryClient = useQueryClient();

  // Sorted so the cache key is order-independent: re-sorting the grid must not
  // look like a different set of assets and trigger a fresh round of signing.
  const key = useMemo(() => [...assetIds].sort(), [assetIds]);

  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: [...ASSET_URLS_QUERY_KEY, key],
    queryFn: () => getAssetUrls(key),
    enabled: key.length > 0,
    // A filter change mostly reshuffles the same assets, so the cards they
    // share keep their images while the new set is signed instead of every
    // card going back to a placeholder. Not when there is nothing to sign:
    // a cleared set means no URLs, not the last set's.
    placeholderData: key.length > 0 ? keepPreviousData : undefined,
    // Derived from the response, not assumed: a cache hit on the backend can
    // return a URL with a minute or two of life left, and treating that as a
    // fresh hour is what lets thumbnails break on screen.
    staleTime: (query) => signedUrlStaleTime(query.state.data?.expires_in_seconds),
    // URLs are derived data, not state — refetching them on every window focus
    // would re-sign a full page of thumbnails each time the user tabs back.
    refetchOnWindowFocus: false,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ASSET_URLS_QUERY_KEY });
  }, [queryClient]);

  return useMemo(
    () => ({
      urlById: new Map((data?.urls ?? []).map(({ asset_id, url }) => [asset_id, url])),
      thumbnailById: new Map(
        (data?.urls ?? []).flatMap(({ asset_id, thumbnail_url }) =>
          thumbnail_url ? [[asset_id, thumbnail_url] as const] : [],
        ),
      ),
      missing: new Set(data?.missing ?? []),
      // Still loading while the previous set stands in: a card that set had
      // no URL for is waiting on this request, not missing one.
      isLoading: key.length > 0 && (isLoading || isPlaceholderData),
      refresh,
    }),
    [data, isLoading, isPlaceholderData, key.length, refresh],
  );
}

/**
 * The URL to show an asset small by: its thumbnail, or the original for an
 * asset that has none yet. Always an image when it is a thumbnail, whatever
 * the asset is — a film's thumbnail is a still.
 */
export function smallUrlOf(urls: AssetUrlLookup, assetId: string): string | undefined {
  return urls.thumbnailById.get(assetId) ?? urls.urlById.get(assetId);
}
